/* eslint-disable max-lines-per-function */
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Icon } from '@equinor/eds-core-react';
import { refresh } from '@equinor/eds-icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  deleteApiV1Delft3dOrchestrationsByOrchestrationId,
  postApiV1ScenariosByScenarioIdStartOrchestration,
} from '../../api/generated';
import { useMsal } from '@azure/msal-react';
import { useFetchScenario } from '../../hooks/useFetchScenario';
import { queryKeys } from '../../hooks/queryKeys';
import { useFetchOrchestration } from '../../hooks/useFetchOrchestration';
import { useFetchOrchestrationProgress } from '../../hooks/useFetchOrchestrationProgress';
import { useFetchCategorizedImages } from '../../hooks/useFetchCategorizedImages';
import { CenteredLoader } from '../../components/CenteredLoader/CenteredLoader';
import { useConvertScenarioToModel } from '../../hooks/useConvertScenarioToModel';
import { SimulationProgress } from '../../features/Simulation/SimulationProgress/SimulationProgress';
import { ConvertToModel } from '../../features/Simulation/ConvertToModel/ConvertToModel';
import { OutputFiles } from '../../features/Simulation/OutputFiles/OutputFiles';
import { GeneratedImages } from '../../features/Simulation/GeneratedImages/GeneratedImages';
import { InputParameters } from '../../features/Simulation/InputParameters';
import { ConfirmDialog } from '../../components/ConfirmDialog/ConfirmDialog';
import { FullPageBanner } from '../../components/FullPageBanner/FullPageBanner';
import * as Styled from './Simulation.styled';

Icon.add({ refresh });

export const Simulation = () => {
  const queryClient = useQueryClient();
  const { simulationId } = useParams<{ simulationId: string }>();
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const roles = accounts[0].idTokenClaims?.roles;
  const [searchParams] = useSearchParams();
  const activeSection =
    searchParams.get('section') === 'parameters' ? 'parameters' : 'progress';
  const [showStopDialog, setShowStopDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const { data: scenario, isPending: scenarioLoading } =
    useFetchScenario(simulationId);
  const { data: orchestration, isLoading: orchestrationLoading } =
    useFetchOrchestration(scenario?.orchestration_id);

  const isProcessing = orchestration?.phase_status === 'Running';
  const isSimulationPhase = orchestration?.current_phase === 'Simulation';

  const { data: progress } = useFetchOrchestrationProgress(
    scenario?.orchestration_id,
    isProcessing && isSimulationPhase,
  );

  const { data: categorizedImages } = useFetchCategorizedImages(
    scenario?.orchestration_id,
    isProcessing,
  );

  const deleteOrchestration = useMutation({
    mutationFn: (orchestrationId: string) =>
      deleteApiV1Delft3dOrchestrationsByOrchestrationId({
        path: { orchestrationId },
      }),
    onSuccess: () => {
      navigate('/models?tab=simulations');
    },
  });

  const startOrchestration = useMutation({
    mutationFn: (scenarioId: string) =>
      postApiV1ScenariosByScenarioIdStartOrchestration({
        path: { scenarioId },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.scenario(simulationId as string),
      });
    },
  });
  const { mutate: convertToModel, isPending: isConverting } =
    useConvertScenarioToModel({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.scenario(simulationId as string),
        });
      },
    });

  const handleStopSimulation = () => {
    setShowStopDialog(true);
  };

  const handleDeleteSimulation = () => {
    setShowDeleteDialog(true);
  };

  const handleStartSimulation = () => {
    if (simulationId) {
      startOrchestration.mutate(simulationId);
    }
  };

  const confirmStopSimulation = () => {
    setShowStopDialog(false);
    if (scenario?.orchestration_id) {
      deleteOrchestration.mutate(scenario.orchestration_id);
    }
  };

  const confirmDeleteSimulation = () => {
    setShowDeleteDialog(false);
    if (scenario?.orchestration_id) {
      deleteOrchestration.mutate(scenario.orchestration_id);
    }
  };

  const handleConvertToModel = () => {
    if (!simulationId) return;
    convertToModel({
      scenarioId: simulationId,
      modelName: scenario?.name ?? undefined,
    });
  };

  const isAdmin = roles?.includes('PEPM.Admin') ?? false;
  const isUser = roles?.includes('PEPM.User') ?? false;
  const isScenarioOwnerOrAdmin =
    isAdmin || (isUser && scenario?.created_by === accounts[0].username);

  if (scenarioLoading) {
    return <CenteredLoader />;
  }

  if (!scenario) {
    return <FullPageBanner message="Scenario not found." />;
  }
  // Determine status based on orchestration state
  // A scenario has no orchestration if orchestration_id is null/undefined
  const hasOrchestration = !!scenario?.orchestration_id;
  const status: 'not-started' | 'running' | 'completed' | 'failed' =
    !hasOrchestration || (!orchestrationLoading && !orchestration)
      ? 'not-started'
      : orchestration?.phase_status === 'Running'
      ? 'running'
      : orchestration?.phase_status === 'Failed'
      ? 'failed'
      : orchestration
      ? 'completed'
      : 'not-started';

  // Extract progress data — use live progress when running, fall back to summary on orchestration when completed
  const progressSummary = orchestration?.simulation_progress;
  const totalSteps = Number(
    progress?.total_timesteps ?? progressSummary?.total_timesteps ?? 0,
  );
  const percentComplete =
    status === 'completed' ? 100 : progress?.percentage_completed ?? 0;
  const currentStep =
    status === 'completed' ? totalSteps : progress?.current_timestep ?? 0;
  const timeRemaining =
    status === 'completed' ? null : progress?.estimated_time_remaining ?? '-';

  return (
    <>
      <ConfirmDialog
        isOpen={showStopDialog}
        header="Stop simulation"
        message="Please confirm that you intend to stop this simulation. All data including the template reference, description and input parameters will be lost."
        danger={true}
        confirmAction={confirmStopSimulation}
        setIsOpen={setShowStopDialog}
      />
      <ConfirmDialog
        isOpen={showDeleteDialog}
        header="Delete simulation"
        message="Please confirm that you intend to delete this failed simulation. All data including the template reference, description and input parameters will be permanently removed."
        danger={true}
        confirmAction={confirmDeleteSimulation}
        setIsOpen={setShowDeleteDialog}
      />

      <Styled.MainContent>
        {activeSection === 'progress' && (
          <Styled.ContentGrid>
            <SimulationProgress
              percentComplete={percentComplete}
              stepsGenerated={currentStep}
              totalSteps={totalSteps}
              timeRemaining={timeRemaining}
              isProcessing={isProcessing}
              status={status}
              onStop={isScenarioOwnerOrAdmin ? handleStopSimulation : undefined}
              onStart={
                isScenarioOwnerOrAdmin && status === 'not-started'
                  ? handleStartSimulation
                  : undefined
              }
              onDelete={
                isScenarioOwnerOrAdmin &&
                !(status === 'completed' && !!scenario.analogue_model_id)
                  ? handleDeleteSimulation
                  : undefined
              }
            />

            <OutputFiles
              orchestrationId={orchestration?.delft_orchestration_id}
              enabled={isSimulationPhase || status === 'completed'}
              shouldPoll={isProcessing && isSimulationPhase}
            />

            {status === 'completed' && !scenario.analogue_model_id && (
              <ConvertToModel
                onConvert={
                  isScenarioOwnerOrAdmin ? handleConvertToModel : undefined
                }
                isConverting={isConverting}
              />
            )}

            {status === 'completed' && !!scenario.analogue_model_id && (
              <ConvertToModel analogueModelId={scenario.analogue_model_id} />
            )}

            <Styled.FullWidthRow>
              <GeneratedImages
                images={categorizedImages}
                orchestrationId={scenario?.orchestration_id ?? undefined}
              />
            </Styled.FullWidthRow>
          </Styled.ContentGrid>
        )}

        {activeSection === 'parameters' && (
          <InputParameters scenario={scenario} />
        )}
      </Styled.MainContent>
    </>
  );
};
